/**
 * Utils
 */
import '@/scripts/utils/polyfil';
import { TweenLite, Power1 } from 'gsap/TweenMax';
import 'EasePack';
import 'ScrollToPlugin';
import '@/scripts/lettering-surrogate-pairs';
import '@/scripts/utils/table-responsive';
import PubSub from 'pubsub-js';
import { EVENTS } from '@/scripts/constants';
import { addTask, runAllTasks } from '@/scripts/utils/idleCallback';
import objectFitImages from 'object-fit-images';
import objectFitVideos from 'object-fit-videos';
import LazyLoad from 'vanilla-lazyload';
import loadFonts from '@/scripts/utils/load-fonts';
import resizeCover from '@/scripts/elements/cover';
import leadZero from '@/scripts/utils/lead-zero';
import swiperSliderInit from '@/scripts/blocks/swiper-slider';
import isIe from '@/scripts/utils/detect-ie';
import FadeSlider from '@/scripts/blocks/FadeSlider';
import BookingMenu from '@/scripts/blocks/bookingMenu';
import limitRows from '@/scripts/utils/limit-rows';
import initGoogleMaps from '@/scripts/utils/googleMaps';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import quicklink from 'quicklink/dist/quicklink.mjs';

/**
 * Modals
 */
import VideoModal from '@/scripts/blocks/VideoModal';
import GalleryModal from '@/scripts/blocks/GalleryModal';

/**
 * Elements
 */
import History from '@/scripts/elements/History';
import HeaderNav from '@/scripts/elements/header-nav';
import Preloader from '@/scripts/elements/Preloader';
import initCoverSlider from '@/scripts/elements/CoverSlider';
import HoverDistortion from '@/scripts/elements/HoverDistortion';
import RollHoverBtn from '@/scripts/elements/RollHoverBtn';
import DoubleTextBtn from '@/scripts/elements/DoubleTextBtn';
import AnimatedListPointer from '@/scripts/elements/AnimatedListPointer';
import AnimatedListLine from '@/scripts/elements/AnimatedListLine';
import HomeServices from '@/scripts/elements/HomeServices';
import { showTime, showDate } from '@/scripts/elements/clock';
import showWeather from '@/scripts/elements/weather';
import Dropdown from '@/scripts/elements/Dropdown';
import PageFilters from '@/scripts/elements/PageFilters';
import TabFilters from '@/scripts/blocks/TabFilters';
import Tabs from '@/scripts/blocks/Tabs';
import Form from '@/scripts/blocks/form/form';

/**
 * Scroll-magic scenes
 */
import '@/scripts/elements/scrollToTop';
import '@/scripts/utils/scrollmagic/scenes/header';
import '@/scripts/utils/scrollmagic/scenes/parallax-img';
import '@/scripts/utils/scrollmagic/scenes/parallax-item';
import '@/scripts/utils/scrollmagic/scenes/counter';
import '@/scripts/utils/scrollmagic/scenes/reveal-images';

/**
 * Animation utils
 */
import selectors from '@/scripts/animation/animation-selectors';
import SectionSecondaryAnimations from '@/scripts/animation/section';


/**
 * Assets
 */
import '@/fonts';
import '@/styles/main.scss';

loadFonts(['Great+Vibes:400:latin', 'Cormorant:400:latin', 'Muli:400,600,700:latin']);
TweenLite.defaultEase = Power1.easeOut;

// configure progress bar

NProgress.configure({
  minimum: 0.1,
  trickleSpeed: 200,
  showSpinner: false,
});

const initBasics = () => new Promise((resolve) => {
  // set css transition-duration to get it in JS
  addTask(() => {
    const duration = getComputedStyle(document.documentElement)['transition-duration'];
    document.documentElement.dataset.duration = parseFloat(duration);
  });


  // Lazy load images
  addTask(() => {
    const lazyLoad = new LazyLoad({
      elements_selector: '[data-lazy-loading="true"]',
    });
    PubSub.subscribe(EVENTS.lazyImages_loadForce, (msg, { lazyImage }) => {
      lazyLoad.load(lazyImage, true);
    });
  });

  addTask(() => {
    if (isIe()) {
      document.querySelectorAll('[data-role="menu"] [data-lazy-loading="true"]').forEach((lazyImage) => {
        // eslint-disable-next-line no-param-reassign
        lazyImage.src = lazyImage.dataset.src;
      });
    }
  });

  // Object fit polyfill
  addTask(() => {
    objectFitImages(); // https://www.npmjs.com/package/object-fit-images
    objectFitVideos(); // https://www.npmjs.com/package/object-fit-videos
  });

  // responsive table
  addTask(() => {
    $('.editable table').responsiveTable();
  });

  // limit text rows
  addTask(() => {
    document.querySelectorAll('[data-limit-rows]').forEach(el => limitRows(el));
  });

  // google maps
  addTask(() => {
    document.querySelectorAll('[data-role*="google-maps-container"]')
      .forEach(map => initGoogleMaps(map));
  });

  // form
  addTask(() => {
    document.querySelectorAll('[data-role*="contact-form"]')
      .forEach(form => new Form(form));
  });

  // scroll-to button
  addTask(() => {
    $('[data-role*="scroll-to"]').on('click', function () { // eslint-disable-line func-names
      const target = $(this).data('target');
      const $targetElement = $(`[data-role="${target}"]`);
      const offset = $targetElement.length && $targetElement.offset().top || 0;
      TweenLite.to(window, 1, { scrollTo: { y: offset, autoKill: false } });
    });
  });

  // init tabs
  addTask(() => {
    document.querySelectorAll('[data-role*="tab-list-container"]')
      .forEach(el => new Tabs({
        mainContainer: el,
        lineController: el.querySelector('[data-role*="animated-list-line"]'),
      }));
  });

  // init tab-filters
  addTask(() => {
    document.querySelectorAll('[data-role*="grid-with-filters"]')
      .forEach(el => new TabFilters({
        el,
        transitionDuration: 700,
        lineController: el.querySelector('[data-role*="animated-list-line"]'),
      }));
  });

  // Main Menu
  addTask(() => {
    new HeaderNav({ // eslint-disable-line no-new
      transitionDuration: 1000,
    });
  });

  // init dropdowns
  addTask(() => {
    document.querySelectorAll('[data-role*="dropdown-container"]')
      .forEach(dropdown => new Dropdown({
        element: dropdown,
      }));
  });

  // init page-filters form
  addTask(() => {
    const pageFilters = new PageFilters({ // eslint-disable-line no-unused-vars
      element: document.querySelector('[data-role*="page-filters-container"]'),
    });
  });

  // 1 -> 01, 11 -> 11
  addTask(() => {
    $('[data-lead-zero="true"] span').text((i, text) => leadZero(text.trim()));
  });

  // split-text
  addTask(() => {
    $('[data-split-text="true"]').each(function trim() {
      const text = $(this).text();
      $(this).text($.trim(text)).lettering('words').children('span')
        .lettering();
    });
  });


  // split-text
  addTask(() => {
    $('[data-split-text="button-letters"]').find('span').each(function trim() {
      const text = $(this).text();
      $(this).text($.trim(text)).lettering();
    });
  });

  // roll-hover buttons
  addTask(() => {
    document.querySelectorAll('[data-role*="roll-hover-btn"]')
      .forEach(button => new RollHoverBtn(button));
  });

  // double-text buttons
  addTask(() => {
    document.querySelectorAll('[data-role*="double-text-btn"]')
      .forEach(button => new DoubleTextBtn(button));
  });

  // list-line forwards active item
  addTask(() => {
    document.querySelectorAll('[data-role*="animated-list-line"]')
      .forEach(list => new AnimatedListLine(list));
  });

  // list-pointer forwards active item
  addTask(() => {
    document.querySelectorAll('[data-role*="animated-list-pointer"]')
      .forEach(list => new AnimatedListPointer(list));
  });

  // init clock
  addTask(() => {
    showTime(document.querySelector('[data-role="time"]'));
    showDate(document.querySelector('[data-role="date"]'));
  });

  // init weather
  addTask(() => {
    showWeather(document.querySelector('[data-role="weather"]'));
  });

  // home-services
  addTask(() => {
    const homeServices = document.querySelectorAll('[data-role*="home-services"]');
    homeServices.forEach(element => new HomeServices({ element }));
  });

  // fade slider
  addTask(() => {
    const fadeSliders = document.querySelectorAll('[data-role*="fade-slider-container"]');
    fadeSliders.forEach(el =>
      new FadeSlider({
        container: el,
        duration: 750,
        itemsToShow: 4,
      }));
  });

  // booking menu
  addTask(() => {
    const bookingForms = document.querySelectorAll('[data-role*="booking-menu-container"]');
    bookingForms
      .forEach(element => new BookingMenu({ element }));
  });

  // resize cover section
  addTask(() => {
    resizeCover();
  });

  // init history module
  addTask(() => {
    const history = new History(); // eslint-disable-line no-unused-vars
  });

  // Current year
  addTask(() => {
    $('.current-year').text((new Date()).getFullYear());
  });

  requestIdleCallback(deadline => runAllTasks(deadline, () => { resolve(true); }), { timeout: 1000 });
});

const initSliders = () => new Promise((resolve) => {
  addTask(() => {
    swiperSliderInit({
      selector: 'another-items',
      slidesPerView: 2,
      breakpoints: {
        639: {
          slidesPerView: 1,
          spaceBetween: 15,
        },
      },
    });
  });

  addTask(() => {
    swiperSliderInit({
      selector: 'mini-gallery',
      slidesPerView: 1,
      breakpoints: {
        639: {
          spaceBetween: 15,
        },
      },
    });
  });

  requestIdleCallback(deadline => runAllTasks(deadline, () => { resolve(true); }), { timeout: 1000 });
});

const initHoverDistortion = () => new Promise((resolve) => {
  addTask(() => {
    document.querySelectorAll('[data-role="hover-distortion"]:not([data-ajax-item="true"])')
      .forEach(element => new HoverDistortion({
        element,
        intensity: 0.2,
      }));
  });

  requestIdleCallback(deadline => runAllTasks(deadline, () => { resolve(true); }), { timeout: 1000 });
});

const initScrollMagic = () => new Promise((resolve) => {
  // build scrollMagic
  addTask(() => {
    const scenes =
      Array
        .from(document.querySelectorAll(selectors.section))
        .map(section => new SectionSecondaryAnimations(section));

    scenes.forEach((scene) => {
      scene.init();
    });
  });

  requestIdleCallback(deadline => runAllTasks(deadline, () => { resolve(true); }), { timeout: 1000 });
});


const initModals = () => new Promise((resolve) => {
  // init video modal element
  addTask(() => {
    const videoModal = new VideoModal({ // eslint-disable-line no-unused-vars
      triggerElements: document.querySelectorAll('[data-role*="video-modal-trigger"]'),
    });
  });

  addTask(() => {
    const galleryModal = new GalleryModal({ // eslint-disable-line no-unused-vars
      triggerElements: document.querySelectorAll('[data-role*="gallery-modal-trigger"]'),
    });
  });

  requestIdleCallback(deadline => runAllTasks(deadline, () => { resolve(true); }), { timeout: 1000 });
});


document.addEventListener('DOMContentLoaded', async () => {
  const preloader = new Preloader({
    element: document.querySelector('[data-role="preloader"]'),
  });
  Preloader.startLoading();
  const loaded = [
    await initBasics(),
    await initCoverSlider({
      element: document.querySelector('[data-role="cover-slider"]'),
    }),
    await initSliders(),
    await initHoverDistortion(),
    await initScrollMagic(),
    await initModals(),
  ];
  if (loaded) {
    requestIdleCallback(() => {
      preloader.play();
      quicklink();
    }, { timeout: 1000 });
  }
});




// WEBPACK FOOTER //
// ./scripts/common.js